import { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import { standardFieldAllowsTargetConditions } from '@risksmart-app/shared/forms/conditionTargets';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ConditionalLogicOption,
  FieldTypeConditionMetadata,
} from 'src/blocks/form-editor-dialog';
import {
  CONDITIONAL_OPERATOR_LABELS,
  FREE_TEXT_CONDITIONAL_OPERATOR_LABELS,
} from 'src/blocks/form-editor-dialog/config';
import { fieldTypesConfig } from 'src/components/form/custom-attributes/field-types';
import type { FieldRendererProps } from 'src/components/form/custom-attributes/renderers/collection-layouts/types';
import {
  FREE_TEXT_CONDITION_OPERATORS,
  isFreeTextConditionType,
  standardFieldSupportsContains,
} from 'src/components/form/edit-field-modal/conditionOperators';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';
import {
  T1_CATEGORY_FIELD_ID,
  useT1CategoryConditionSource,
} from 'src/pages/risks/useT1CategoryConditionSource';
import { useFormConfigRegistry } from 'src/utils/table/hooks/form/useFormConfigRegistry';
import { getCustomAttributeRenderProps } from 'src/utils/table/utils/customAttributes';

import { useGetDepartments, useGetUsers } from '@/hooks/queries';
import { useGetTags } from '@/hooks/queries';

import { buildLowercaseIndex } from './conditionPropertyKeys';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { useFormFieldRegistryStore } from './stores/useFormFieldRegistryStore';

const FALLBACK_OPERATOR_OPTIONS: { value: string; label: string }[] = [
  { value: '=', label: CONDITIONAL_OPERATOR_LABELS['='] },
  { value: '!=', label: CONDITIONAL_OPERATOR_LABELS['!='] },
];

const mapDeclaredOperators = (
  operators: readonly (string | { operator: string })[]
): { value: string; label: string }[] =>
  operators
    .map((op) => {
      const opValue = typeof op === 'string' ? op : op.operator;

      return {
        value: opValue,
        label: CONDITIONAL_OPERATOR_LABELS[opValue] ?? opValue,
      };
    })
    .filter((o) => o.value in CONDITIONAL_OPERATOR_LABELS);

// Derived from `FREE_TEXT_CONDITION_OPERATORS` so the picker can never offer an
// operator the evaluated property does not declare.
const FREE_TEXT_OPERATOR_OPTIONS: { value: string; label: string }[] =
  mapDeclaredOperators(FREE_TEXT_CONDITION_OPERATORS).map((option) => ({
    ...option,
    label: FREE_TEXT_CONDITIONAL_OPERATOR_LABELS[option.value] ?? option.label,
  }));

const defaultOperatorOptions = (
  type: string | undefined
): { value: string; label: string }[] =>
  isFreeTextConditionType(type)
    ? FREE_TEXT_OPERATOR_OPTIONS
    : FALLBACK_OPERATOR_OPTIONS;

interface UseFormEditorConditionalOptionsResult {
  fieldOptions: ConditionalLogicOption[];
  getValueOptions: (fieldId: string) => ConditionalLogicOption[];
  /**
   * Resolves per-field-type metadata so the dialog can decide source
   * eligibility for in-session-new fields (e.g. a freshly-added text field
   * usable as a free-text source, or a userMultiselect with server-derived
   * value options) before save.
   */
  getFieldTypeMetadata: (
    type: string
  ) => FieldTypeConditionMetadata | undefined;
}

/**
 * Builds the conditional-logic source picker options for the form editor
 * dialog, mirroring the eligibility rules of the inline edit-field modal:
 *
 * - **allowAsSource**: a field can be the basis of a rule only when it has an
 *   enumerable value list (i.e. checkboxes can be rendered). For standard
 *   fields that means `allowAsConditionSource` plus a `displayType` whose
 *   values can be enumerated; for custom fields it means
 *   `fieldTypesConfig[type].allowAsConditionSource` and a non-empty
 *   `getConditionalPropertyFilterOptions` result.
 * - **allowAsTarget**: true for custom fields, or standard fields with
 *   `allowTargetConditions` — or, failing that, standard fields carrying a
 *   code-declared `defaultConditions` (whose rule the editor must surface).
 *   `EditFieldFields.tsx` reads `allowTargetConditions` directly and so does not
 *   pick up the `defaultConditions` fallback.
 *
 * `getValueOptions` returns the same enumerated values for any field — empty
 * for non-source fields. The dialog already prefers in-session edited options
 * over this fallback.
 */
export const useFormEditorConditionalOptions = (
  parentType: FormId,
  enabled: boolean
): UseFormEditorConditionalOptionsResult => {
  const formRegistry = useFormConfigRegistry();
  const formConfig = formRegistry[parentType];
  const helpers = useCustomDatasourceHelpers();
  const registeredFields = useFormFieldRegistryStore(
    (state) => state.getAllFields
  );
  const { customAttributeSchema } = useCustomisableFormDataContext();

  const { data: usersData } = useGetUsers({
    queryArgs: {},
    shouldSkip: !enabled,
  });
  const { data: departmentsData } = useGetDepartments({
    queryArgs: {},
    shouldSkip: !enabled,
  });
  const { data: tagsData } = useGetTags({
    queryArgs: {},
    shouldSkip: !enabled,
  });

  const users = useMemo(() => usersData?.user ?? [], [usersData]);
  const departmentTypes = useMemo(
    () => departmentsData?.department_type ?? [],
    [departmentsData]
  );
  const tagTypes = useMemo(() => tagsData?.tag_type ?? [], [tagsData]);

  const renderPropsByFieldId = useMemo(() => {
    if (!customAttributeSchema?.Schema || !customAttributeSchema?.UiSchema) {
      return new Map<string, FieldRendererProps>();
    }

    return new Map(
      getCustomAttributeRenderProps(
        customAttributeSchema.Schema,
        customAttributeSchema.UiSchema
      ).map((rp) => [rp.scope, rp])
    );
  }, [customAttributeSchema]);

  const standardFieldByFieldId = useMemo(() => {
    if (!formConfig) {
      return new Map<string, FormFieldConfig>();
    }

    return buildLowercaseIndex(
      Object.values(formConfig) as FormFieldConfig[],
      (field) => field.fieldId
    );
  }, [formConfig]);

  const computeOperatorOptions = useCallback(
    (fieldId: string): { value: string; label: string }[] | undefined => {
      const customRenderProps = renderPropsByFieldId.get(fieldId);
      if (customRenderProps) {
        const fieldTypeConfig = fieldTypesConfig[customRenderProps.type];
        const filterProperty =
          fieldTypeConfig?.getConditionalPropertyFilterProperty?.(
            customRenderProps,
            { users, departmentTypes, tagTypes }
          );
        if (filterProperty?.operators && filterProperty.operators.length > 0) {
          return mapDeclaredOperators(filterProperty.operators);
        }
        if (fieldTypeConfig?.allowAsConditionSource) {
          return defaultOperatorOptions(customRenderProps.type);
        }

        return undefined;
      }

      const standard = standardFieldByFieldId.get(fieldId.toLowerCase());
      if (standard?.allowAsConditionSource) {
        // Lookup sources (commonLookup, rating, users, tags, …) keep = / !=.
        return standardFieldSupportsContains(standard)
          ? FREE_TEXT_OPERATOR_OPTIONS
          : FALLBACK_OPERATOR_OPTIONS;
      }

      return undefined;
    },
    [
      renderPropsByFieldId,
      standardFieldByFieldId,
      users,
      departmentTypes,
      tagTypes,
    ]
  );

  /**
   * Computes the enumerable value options for a field.
   *
   * - `null` → field is not source-eligible at all.
   * - `[]` (with `freeText: true`) → field is source-eligible but has no
   *   enumerable values (free-text only — e.g. plain text/title fields).
   * - `[...]` → field is source-eligible with a concrete value list.
   */
  const computeValueOptions = useCallback(
    (
      fieldId: string
    ): { options: ConditionalLogicOption[]; freeText: boolean } | null => {
      const customRenderProps = renderPropsByFieldId.get(fieldId);
      if (customRenderProps) {
        const fieldTypeConfig = fieldTypesConfig[customRenderProps.type];
        if (!fieldTypeConfig?.allowAsConditionSource) {
          return null;
        }
        const filterOptions =
          fieldTypeConfig.getConditionalPropertyFilterOptions?.(
            customRenderProps,
            { users, departmentTypes, tagTypes }
          );
        if (!filterOptions) {
          // Source-eligible but no enumerable values → free text.
          return { options: [], freeText: true };
        }

        return {
          options: filterOptions
            .filter(
              (o): o is { value: string; label: string } =>
                o.value != null && o.label != null
            )
            .map((o) => ({ value: o.value, label: o.label })),
          freeText: false,
        };
      }

      const standard = standardFieldByFieldId.get(fieldId.toLowerCase());
      if (!standard?.allowAsConditionSource) {
        return null;
      }
      const displayType = standard.displayType;
      if (!displayType) {
        // No displayType → free text.
        return { options: [], freeText: true };
      }

      switch (displayType.displayType) {
        case 'commonLookup':
          return {
            options: helpers
              .getCommonLookupOptions(displayType.i18nKey)
              .map((o: { value: string | number; label: string }) => ({
                value: String(o.value),
                label: o.label,
              })),
            freeText: false,
          };
        case 'rating': {
          if (
            helpers.hasScoringSettings &&
            displayType.ratingKey === 'likelihood'
          ) {
            return {
              options: helpers.likelihoodOptions.map(
                (o: { value: number; label: string }) => ({
                  value: String(o.value),
                  label: o.label,
                })
              ),
              freeText: false,
            };
          }
          if (
            helpers.hasScoringSettings &&
            displayType.ratingKey === 'impact'
          ) {
            return {
              options: helpers.impactOptions.map(
                (o: { value: number; label: string }) => ({
                  value: String(o.value),
                  label: o.label,
                })
              ),
              freeText: false,
            };
          }

          return {
            options: helpers
              .getRatingOptions(displayType.ratingKey)
              .filter(
                (o): o is typeof o & { value: string | number } =>
                  o.value != null
              )
              .map((o) => ({ value: String(o.value), label: o.label })),
            freeText: false,
          };
        }
        case 'users':
          return {
            options: users
              .filter(
                (u): u is typeof u & { Id: string } => typeof u.Id === 'string'
              )
              .map((u) => ({
                value: u.Id,
                label: u.FriendlyName ?? u.Email ?? u.Id,
              })),
            freeText: false,
          };
        case 'departments':
          return {
            options: departmentTypes.map((d) => ({
              value: d.DepartmentTypeId,
              label: d.Name,
            })),
            freeText: false,
          };
        case 'tags':
          return {
            options: tagTypes.map((t) => ({
              value: t.TagTypeId,
              label: t.Name,
            })),
            freeText: false,
          };
        default:
          // No enumeration for this displayType → free text.
          return { options: [], freeText: true };
      }
    },
    [
      renderPropsByFieldId,
      standardFieldByFieldId,
      users,
      departmentTypes,
      tagTypes,
      helpers,
    ]
  );

  // CUS-242 — T1 Category is a synthetic source: the record's nearest Tier-1
  // ancestor, not a registered form field, so `registeredFields()` will never
  // yield it and it has to be appended explicitly.
  //
  // V3 only, and gated identically to the runtime in
  // `useT1CategoryConditionSource` — offering it in the editor while the runtime
  // could not evaluate it would let an admin author a rule that silently never
  // matches.
  const { t: tColumns } = useTranslation(['common'], {
    keyPrefix: 'risks.columns',
  });

  // Same hook the runtime uses, so the picker and the evaluator cannot offer
  // different sources or different values. It owns the flag gate and the fetch;
  // this hook only maps its declaration into picker shape.
  const { additionalField: t1CategoryField } = useT1CategoryConditionSource({
    formId: parentType,
    isEnabled: enabled,
    label: tColumns('t1_category'),
  });
  const isT1CategoryEnabled = !!t1CategoryField;

  // The rule stores the Tier-1 risk's Id; the Title is only its label. Titles
  // are not unique on `risk` and change on rename, so storing one would both
  // break on rename and match records under a same-named sibling.
  const t1CategoryValueOptions = useMemo<ConditionalLogicOption[]>(
    () =>
      (t1CategoryField?.options ?? []).map((option) => ({
        value: option.id,
        label: option.label,
      })),
    [t1CategoryField]
  );

  const fieldOptions = useMemo<ConditionalLogicOption[]>(() => {
    const seen = new Set<string>();
    const result: ConditionalLogicOption[] = [];

    for (const reg of registeredFields()) {
      const lower = reg.fieldId.toLowerCase();
      if (seen.has(lower)) {
        continue;
      }
      seen.add(lower);

      const customRenderProps = renderPropsByFieldId.get(reg.fieldId);
      const standard = standardFieldByFieldId.get(lower);
      const isGroup = !reg.isCustom && !standard;

      const label =
        customRenderProps?.label ??
        standard?.formLabel ??
        reg.label ??
        reg.fieldId;

      const type =
        customRenderProps?.type ?? standard?.displayType?.displayType;

      const valueOptions = isGroup ? null : computeValueOptions(reg.fieldId);
      const allowAsSource = valueOptions !== null;

      const allowAsTarget = isGroup
        ? false
        : reg.isCustom
          ? true
          : standardFieldAllowsTargetConditions(standard);

      result.push({
        value: reg.fieldId,
        label,
        type,
        allowAsSource,
        allowAsTarget,
        freeText: valueOptions?.freeText ?? false,
        operatorOptions: allowAsSource
          ? computeOperatorOptions(reg.fieldId)
          : undefined,
      });
    }

    if (isT1CategoryEnabled) {
      result.push({
        value: T1_CATEGORY_FIELD_ID,
        label: tColumns('t1_category'),
        type: CustomAttributeFieldType.Select,
        allowAsSource: true,
        // It is a derived value with nothing to show or hide, so it can be the
        // basis of a rule but never its target.
        allowAsTarget: false,
        freeText: false,
        // The same operator pair a select custom field declares — single-select
        // semantics, per the ticket's "consistent with the existing pattern".
        operatorOptions: FALLBACK_OPERATOR_OPTIONS,
        // Not a field on the canvas, so the dialog would otherwise drop it when
        // it builds the picker from the fields it is editing.
        isSynthetic: true,
      });
    }

    return result;
  }, [
    registeredFields,
    renderPropsByFieldId,
    standardFieldByFieldId,
    computeValueOptions,
    computeOperatorOptions,
    isT1CategoryEnabled,
    tColumns,
  ]);

  const getValueOptions = useCallback(
    (fieldId: string): ConditionalLogicOption[] =>
      fieldId === T1_CATEGORY_FIELD_ID
        ? t1CategoryValueOptions
        : (computeValueOptions(fieldId)?.options ?? []),
    [computeValueOptions, t1CategoryValueOptions]
  );

  const getFieldTypeMetadata = useCallback(
    (type: string): FieldTypeConditionMetadata | undefined => {
      const config =
        fieldTypesConfig[type as CustomAttributeFieldType] ?? undefined;
      if (!config) {
        return undefined;
      }

      const allowAsConditionSource = config.allowAsConditionSource === true;

      // Probe the type's providers with a minimal stub.
      const stubRenderProps: FieldRendererProps = {
        type: type as CustomAttributeFieldType,
        label: '',
        scope: '',
        path: '',
        options: [],
      };

      // null result → free-text capture.
      let defaultValueOptions: ConditionalLogicOption[] | null = null;
      if (config.getConditionalPropertyFilterOptions) {
        const opts = config.getConditionalPropertyFilterOptions(
          stubRenderProps,
          { users, departmentTypes, tagTypes }
        );
        defaultValueOptions = (opts ?? [])
          .filter(
            (o): o is { value: string; label: string } =>
              o.value != null && o.label != null
          )
          .map((o) => ({ value: o.value, label: o.label }));
      }

      const filterProperty = config.getConditionalPropertyFilterProperty?.(
        stubRenderProps,
        { users, departmentTypes, tagTypes }
      );
      const operatorOptions =
        filterProperty?.operators && filterProperty.operators.length > 0
          ? mapDeclaredOperators(filterProperty.operators)
          : allowAsConditionSource
            ? defaultOperatorOptions(type)
            : undefined;

      return { allowAsConditionSource, defaultValueOptions, operatorOptions };
    },
    [users, departmentTypes, tagTypes]
  );

  return { fieldOptions, getValueOptions, getFieldTypeMetadata };
};
